import { useEffect, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Loader2, ClipboardCheck } from "lucide-react";
import { apiGet } from "@/lib/api";
import { cn } from "@/lib/utils";
import { MobileSectionSelect } from "@/components/layout/mobile-section-select";
import { ChatPanel, useChatUnreadCount } from "@/components/messages/chat-panel";
import { CrewNotesPanel } from "@/components/messages/crew-notes-panel";
import { CrewCredentialsPanel } from "@/components/supervisor/crew-credentials-panel";
import { AssignmentsPage, validateAssignmentsSearch, type CertAssignPrefill } from "@/routes/_authenticated/admin/assignments";

export const Route = createFileRoute("/_authenticated/supervisor")({
  component: SupervisorPage,
  validateSearch: (s: Record<string, unknown>) => ({
    ...validateAssignmentsSearch(s),
    // Which part of the Supervisor area is open (the sidebar's sub-list links here)
    section: s.section === "credentials" || s.section === "messages" ? (s.section as "credentials" | "messages") : undefined,
  }),
});

function SupervisorPage() {
  const { user } = Route.useRouteContext();
  const { section } = Route.useSearch();
  const isAdminRole = user.role === "admin" || user.role === "div_admin";

  const { data: me, isLoading } = useQuery({
    queryKey: ["me"],
    queryFn: () => apiGet<{ isSupervisor?: boolean; supervisorDivisionIds?: string[] }>("/api/me"),
  });
  const { data: divisions } = useQuery({
    queryKey: ["divisions-basic"],
    queryFn: () => apiGet<{ data: { id: string; name: string }[] }>("/api/divisions"),
  });

  if (isLoading) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!me?.isSupervisor && !isAdminRole) {
    return (
      <div className="max-w-lg mx-auto py-16 text-center space-y-3">
        <ClipboardCheck className="h-8 w-8 text-muted-foreground/40 mx-auto" />
        <p className="text-sm text-muted-foreground">
          This page is for division supervisors. Ask an admin to make you a supervisor of your division.
        </p>
        <Link to="/" className="text-sm text-paris-red-text">
          Back to Home
        </Link>
      </div>
    );
  }

  const mine = new Set(me?.supervisorDivisionIds ?? []);
  const names = (divisions?.data ?? []).filter((d) => mine.has(d.id)).map((d) => d.name);

  return (
    <SupervisorTabs names={names} section={section} />
  );
}

function SupervisorTabs({ names, section }: { names: string[]; section?: "credentials" | "messages" }) {
  const [tab, setTab] = useState<"assignments" | "credentials" | "messages">(section ?? "assignments");
  useEffect(() => {
    setTab(section ?? "assignments");
  }, [section]);
  const [messagesTab, setMessagesTab] = useState<"notes" | "chat">("notes");
  // "Assign to the people missing it" jumps to Assignments with the form pre-filled.
  const [assignPrefill, setAssignPrefill] = useState<CertAssignPrefill | null>(null);

  const navigate = useNavigate();
  const { data: unread } = useChatUnreadCount();
  const unreadCount = unread?.count ?? 0;

  const tabBtn = (active: boolean) =>
    cn(
      "px-3 py-1.5 rounded-md text-sm font-medium transition-colors",
      active ? "bg-surface-raised text-text-primary shadow-sm" : "text-muted-foreground hover:text-text-primary"
    );

  return (
    <div>
      <div className="mx-auto max-w-4xl px-4 pt-4">
        <div className="flex items-center gap-2">
          <ClipboardCheck className="h-5 w-5 text-paris-red-text" />
          <h1 className="text-2xl font-bold text-text-primary">Supervisor</h1>
        </div>
        <Link to="/subcontractors" className="mt-1 inline-block text-sm text-paris-red-text underline underline-offset-2">
          Subcontractors on your projects
        </Link>
        <p className="text-sm text-muted-foreground mt-1">
          {names.length > 0 ? `Supervising: ${names.join(", ")}. ` : ""}
          Assign documents and certifications to your division's crew, check their credentials, and message your crew and admins.
        </p>
        {/* Phones: a drop-down list instead of a row that scrolls sideways */}
        <MobileSectionSelect
          className="mt-3"
          label="Supervisor section"
          value={tab}
          options={[
            { value: "assignments", label: "Assignments" },
            { value: "credentials", label: "Crew credentials" },
            { value: "messages", label: `Messages${unreadCount > 0 ? ` · ${unreadCount} new` : ""}` },
            { value: "toolbox", label: "Toolbox" },
          ]}
          onChange={(v) => (v === "toolbox" ? navigate({ to: "/toolbox" }) : setTab(v as "assignments" | "credentials" | "messages"))}
        />
        <div className="mt-3 hidden gap-1 rounded-lg border border-border p-1 bg-muted/30 w-fit md:flex">
          <button type="button" onClick={() => setTab("assignments")} className={tabBtn(tab === "assignments")}>
            Assignments
          </button>
          <button type="button" onClick={() => setTab("credentials")} className={tabBtn(tab === "credentials")}>
            Crew credentials
          </button>
          <button type="button" onClick={() => setTab("messages")} className={tabBtn(tab === "messages")}>
            Messages{unreadCount > 0 ? ` · ${unreadCount} new` : ""}
          </button>
          <Link to="/toolbox" className={tabBtn(false)}>
            Toolbox
          </Link>
        </div>
      </div>

      {tab === "assignments" && <AssignmentsPage prefill={assignPrefill} onPrefillUsed={() => setAssignPrefill(null)} />}

      {tab === "credentials" && (
        <div className="mx-auto max-w-4xl px-4 py-4">
          <CrewCredentialsPanel
            onAssign={(certTypeId, workerIds) => {
              setAssignPrefill({ certTypeId, workerIds });
              setTab("assignments");
            }}
          />
        </div>
      )}

      {tab === "messages" && (
        <div className="mx-auto max-w-4xl px-4 py-4 space-y-4">
          <div className="flex gap-1 rounded-lg border border-border p-1 bg-muted/30 w-fit">
            <button type="button" onClick={() => setMessagesTab("notes")} className={tabBtn(messagesTab === "notes")}>
              Crew notes
            </button>
            <button type="button" onClick={() => setMessagesTab("chat")} className={tabBtn(messagesTab === "chat")}>
              Admin chat{unreadCount > 0 ? ` · ${unreadCount} new` : ""}
            </button>
          </div>
          {messagesTab === "notes" ? <CrewNotesPanel /> : <ChatPanel />}
        </div>
      )}
    </div>
  );
}
